import { LoadingState } from "@/components/board/LoadingState";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useOoo } from "@/contexts/OooContext";
import type { EmployeeSummary } from "@/hooks/useMakersBoardData";
import { useRoles } from "@/hooks/useRoles";
import { useUserSettings } from "@/contexts/UserSettingsContext";
import { fullRowChanges, recordAudit } from "@/lib/audit";
import { isBambooFallbackRole, roleIdsToNames } from "@/lib/role-formatting";
import { supabase } from "@/lib/supabase";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ErrorBanner } from "../board/ErrorBanner";
import { AddEmployeeModal } from "./AddEmployeeModal";
import { EmployeeSearch, getMatchScore } from "./EmployeeSearch";
import {
  MakersBoardActions,
  type SyncActionResult,
} from "./MakersBoardActions";
import { MakersTable, type SortDirection } from "./MakersTable";
import { AvailabilityModeToggle } from "@/components/common/AvailabilityModeToggle";
import type { AvailabilityMode } from "@/utils/makersUtils";
import { getEmployeeDisplayName } from "@/utils/nameUtils";
import { MousePointer2 } from "lucide-react";

interface MakersBoardProps {
  employees: EmployeeSummary[];
  loading: boolean;
  error: string | null;
  selectedOffices: string[];
  updateCapacityOptimistically: (
    employeeId: string,
    newCapacity: number,
  ) => void;
  updateEmployeeCapacity: (
    employeeId: string,
    newCapacity: number,
  ) => Promise<void>;
  updateEmployeeRoles: (
    employeeId: string,
    roles: import("@/hooks/useMakersBoardData").EmployeeRole[],
  ) => Promise<void>;
  updateRolesOptimistically: (
    employeeId: string,
    roles: import("@/hooks/useMakersBoardData").EmployeeRole[],
  ) => void;
  updateNameOptimistically: (employeeId: string, newName: string) => void;
  updateEmployeeName: (employeeId: string, newName: string) => Promise<void>;
  updateNicknameOptimistically: (employeeId: string, nickname: string | null) => void;
  updateEmployeeNickname: (employeeId: string, nickname: string | null) => Promise<void>;
  startDate: Date;
  numWeeks: number;
  onEmployeeClick?: (employeeId: string) => void;
  selectedEmployeeIds?: Set<string>; // Optional: Set of selected employee IDs
  /** Initial role filter. When undefined (makers page), defaults to every role except the bamboo fallback. When set (e.g. Find People modal), uses that value. */
  initialSelectedRoles?: string[];
  /** When true, role column is read-only (e.g. Find People modal). Avoids showing editable role UI that would no-op. */
  roleEditingDisabled?: boolean;
  /** Optional callback to refresh employee data (e.g. after adding a new employee). When absent, page reload is used. */
  onRefresh?: () => Promise<void>;
  /** When true, the delete button is shown for manually-created employees. */
  showDeleteForManual?: boolean;
  /** Optional initial pill search terms (e.g. names pre-seeded from a deep link or modal context). */
  initialSearchPills?: string[];
  /** Optional extra filter control (e.g. Find People's availability checkbox). When set, it renders in a
   * filter row below the search bar alongside the availability-mode toggle, which moves out of the table header. */
  availabilityToggle?: ReactNode;
  /** Controlled availability mode. When both this and `onAvailabilityModeChange` are
   * provided (the Find People modal, which co-owns the mode with its candidate
   * availability filter so the two stay consistent), the toggle is rendered as
   * controlled. Otherwise the mode is internal, seeded from UserSettingsContext
   * (the makers page). */
  availabilityMode?: AvailabilityMode;
  onAvailabilityModeChange?: (mode: AvailabilityMode) => void;
}

/**
 * MakersBoard component - displays employee data with filtering and sorting
 * This component handles data logic and composition, layout is handled by the page
 */
export function MakersBoard({
  employees,
  loading,
  error,
  selectedOffices,
  selectedEmployeeIds,
  onEmployeeClick,
  updateCapacityOptimistically,
  updateEmployeeCapacity,
  updateEmployeeRoles,
  updateRolesOptimistically,
  updateNameOptimistically,
  updateEmployeeName,
  updateNicknameOptimistically,
  updateEmployeeNickname,
  startDate,
  numWeeks,
  initialSelectedRoles,
  roleEditingDisabled = false,
  onRefresh,
  showDeleteForManual = false,
  initialSearchPills,
  availabilityToggle,
  availabilityMode: controlledAvailabilityMode,
  onAvailabilityModeChange,
}: MakersBoardProps) {
  const { roles, formatRole } = useRoles();
  const { settings, loading: settingsLoading } = useUserSettings();
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    () => initialSelectedRoles ?? [],
  );
  const [searchPills, setSearchPills] = useState<string[]>(
    () => initialSearchPills ?? [],
  );
  const [searchInput, setSearchInput] = useState("");

  /** All role ids so the filter always includes Tech Lead, Dev Support, etc. (sorted by sort_order). */
  const roleOptions = useMemo(() => roles.map((r) => r.id), [roles]);

  // Apply the default role filter once roles load (makers page only — when not
  // externally controlled via initialSelectedRoles). Defaults to every role
  // except the bamboo fallback (Atom).
  const appliedDefaultRoles = useRef(false);
  useEffect(() => {
    if (
      initialSelectedRoles !== undefined ||
      appliedDefaultRoles.current ||
      roles.length === 0
    )
      return;
    setSelectedRoles(
      roles.filter((r) => !isBambooFallbackRole(r)).map((r) => r.id),
    );
    appliedDefaultRoles.current = true;
  }, [roles, initialSelectedRoles]);

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      }
      return [...prev, role];
    });
  };

  const clearRoleFilter = () => setSelectedRoles([]);

  // Controlled when the parent supplies both the value and the change handler
  // (Find People modal). Otherwise the mode is internal to this board.
  const isModeControlled =
    controlledAvailabilityMode !== undefined && onAvailabilityModeChange !== undefined;
  const [internalMode, setInternalMode] = useState<AvailabilityMode>("all");
  const availabilityMode = isModeControlled ? controlledAvailabilityMode : internalMode;
  const setAvailabilityMode = isModeControlled ? onAvailabilityModeChange : setInternalMode;
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Seed the uncontrolled internal mode from the user's saved default once it
  // loads (makers page). No "preserve" toggle, so an in-session change reverts on
  // reload. Skipped when controlled — the parent owns the value (and, like the
  // Find People modal, may render outside the provider tree where useUserSettings
  // only returns loading defaults).
  const appliedAvailabilityModeDefault = useRef(false);
  useEffect(() => {
    if (isModeControlled) return;
    if (appliedAvailabilityModeDefault.current || settingsLoading) return;
    setInternalMode(settings.availabilityMode);
    appliedAvailabilityModeDefault.current = true;
  }, [isModeControlled, settingsLoading, settings]);

  const handleSort = () => {
    setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
  };

  const activeSearchTerms = useMemo(() => {
    const terms = [...searchPills];
    const trimmed = searchInput.trim();
    if (trimmed) terms.push(trimmed);
    return terms;
  }, [searchPills, searchInput]);

  const selectionAnchorRef = useRef<Set<string> | null>(null);
  const selectionAnchorKeyRef = useRef<string>("");
  const hasLoadedRef = useRef(false);
  const previousEmployeesRef = useRef<EmployeeSummary[] | null>(null);

  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      // Always include selected employees (e.g. in Find People modal) so they stay visible and pinned at top
      if (selectedEmployeeIds?.has(employee.id)) {
        return true;
      }

      if (activeSearchTerms.length > 0) {
        const matchesAny = activeSearchTerms.some(
          (term) => getMatchScore(employee, term) > 0,
        );
        if (!matchesAny) return false;
      }

      if (selectedRoles.length > 0) {
        const empRoles = employee.role ?? [];
        if (
          empRoles.length === 0 ||
          !empRoles.some((r) => selectedRoles.includes(r))
        ) {
          return false;
        }
      }

      if (selectedOffices.length > 0 && selectedOffices[0] !== "all") {
        if (
          !employee.office_id ||
          !selectedOffices.includes(employee.office_id)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    employees,
    selectedRoles,
    selectedOffices,
    activeSearchTerms,
    selectedEmployeeIds,
  ]);

  const selectedRolesKey = selectedRoles.join("|");
  const selectedOfficesKey = selectedOffices.join("|");
  const selectionAnchorKey = [
    sortDirection,
    selectedRolesKey,
    selectedOfficesKey,
  ].join("::");

  const didEmployeesChange = previousEmployeesRef.current !== employees;
  if (didEmployeesChange) {
    previousEmployeesRef.current = employees;
  }

  const shouldInitAfterLoad = !loading && !hasLoadedRef.current;
  if (shouldInitAfterLoad) {
    hasLoadedRef.current = true;
  }

  const shouldUpdateSelectionAnchor =
    !loading &&
    (shouldInitAfterLoad ||
      didEmployeesChange ||
      selectionAnchorKeyRef.current !== selectionAnchorKey);

  if (shouldUpdateSelectionAnchor) {
    selectionAnchorKeyRef.current = selectionAnchorKey;
    selectionAnchorRef.current = selectedEmployeeIds
      ? new Set(selectedEmployeeIds)
      : null;
  }

  const sortedEmployees = useMemo(() => {
    const sorted = [...filteredEmployees];
    sorted.sort((a, b) => {
      const selectionAnchor = selectionAnchorRef.current;
      if (selectionAnchor && selectionAnchor.size > 0) {
        const aSelected = selectionAnchor.has(a.id);
        const bSelected = selectionAnchor.has(b.id);
        if (aSelected !== bSelected) {
          return aSelected ? -1 : 1;
        }
      }

      const comparison = getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b));

      const primary = sortDirection === "asc" ? comparison : -comparison;
      if (primary !== 0) return primary;

      const nameTieBreak = (a.name || "").localeCompare(b.name || "");
      if (nameTieBreak !== 0) return nameTieBreak;

      return a.id.localeCompare(b.id);
    });
    return sorted;
  }, [filteredEmployees, sortDirection]);

  const [showAddEmployee, setShowAddEmployee] = useState(false);

  const handleEmployeeAdded = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  }, [onRefresh]);

  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const {
    enabled: oooEnabled,
    editMode: oooEditMode,
    setEditMode: setOooEditMode,
    refresh: refreshOoo,
  } = useOoo();

  const handleSyncEmployees = useCallback(async (): Promise<SyncActionResult> => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "sync-bamboohr-employees",
        { method: "POST", body: {} },
      );
      if (fnError) return { success: false, message: fnError.message };

      const result = {
        success: data?.success ?? true,
        message:
          data?.message ||
          `Created: ${data?.created ?? 0}, Updated: ${data?.updated ?? 0}`,
      };
      if (result.success && onRefresh) {
        try {
          await onRefresh();
        } catch (refreshError) {
          console.error("Employee sync completed, but the board refresh failed:", refreshError);
        }
      }
      return result;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }, [onRefresh]);

  const handleSyncCalendar = useCallback(async (): Promise<SyncActionResult> => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "sync-ooo-from-gcal",
        { method: "POST", body: {} },
      );
      if (fnError) return { success: false, message: fnError.message };

      const result = {
        success: data?.success ?? true,
        message:
          data?.message ||
          `Created: ${data?.created ?? 0}, Updated: ${data?.updated ?? 0}, Deleted: ${data?.deleted ?? 0}`,
      };
      if (result.success) {
        try {
          await refreshOoo();
        } catch (refreshError) {
          console.error("Calendar sync completed, but OOO refresh failed:", refreshError);
        }
      }
      return result;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }, [refreshOoo]);

  const handleDeleteEmployee = useCallback(
    async (employeeId: string) => {
      if (!window.confirm("Are you sure you want to delete this employee?"))
        return;
      markSelfEdit("employees", employeeId);
      const oldEmployee = employees.find((e) => e.id === employeeId);
      const { error: deleteError } = await supabase
        .from("employees")
        .delete()
        .eq("id", employeeId);
      if (deleteError) {
        console.error("Failed to delete employee:", deleteError);
        return;
      }
      if (oldEmployee) {
        const { office, weeklyCapacityHours, assignments, ...employeeRow } =
          oldEmployee;
        const changes = fullRowChanges(employeeRow, "DELETE");
        if (changes.role) {
          changes.role = {
            old: roleIdsToNames(employeeRow.role, formatRole),
            new: null,
          };
        }
        recordAudit({
          table: "employees",
          recordId: employeeId,
          operation: "DELETE",
          changes,
          recordLabel: oldEmployee.name ?? null,
          officeId: oldEmployee.office_id ?? null,
        });
      }
      handleEmployeeAdded();
    },
    [handleEmployeeAdded, markSelfEdit, employees, formatRole],
  );

  return (
    <>
      {error ? <ErrorBanner message={error} entityName="employees" /> : null}

      {loading ? (
        <LoadingState message="Loading employees..." />
      ) : (
        <div className="board-content board-content--fill">
          <div className="makers-board__search-row">
            <EmployeeSearch
              pills={searchPills}
              onPillsChange={setSearchPills}
              inputValue={searchInput}
              onInputChange={setSearchInput}
              placeholder="Search employees by name..."
            />
            <MakersBoardActions
              oooEnabled={oooEnabled}
              oooEditMode={oooEditMode}
              onAddEmployee={() => setShowAddEmployee(true)}
              onToggleOoo={() => setOooEditMode(!oooEditMode)}
              syncEmployees={handleSyncEmployees}
              syncCalendar={handleSyncCalendar}
            />
          </div>
          {oooEnabled && oooEditMode && (
            <div className="makers-board__ooo-hint" role="status">
              <span className="makers-board__ooo-hint-icon" aria-hidden>
                <MousePointer2 />
              </span>
              <span className="makers-board__ooo-hint-copy">
                <strong>OOO edit mode</strong>
                <span>Click a week or drag across weeks, then type hours.</span>
              </span>
            </div>
          )}
          {availabilityToggle && (
            <div className="makers-board__filter-row">
              <div className="makers-board__toggle makers-board__toggle--mode">
                Show
                <AvailabilityModeToggle
                  mode={availabilityMode}
                  onChange={setAvailabilityMode}
                />
              </div>
              {availabilityToggle}
            </div>
          )}
          <MakersTable
            employees={sortedEmployees}
            startDate={startDate}
            numWeeks={numWeeks}
            sortDirection={sortDirection}
            toggleSortDirection={handleSort}
            selectedRoles={selectedRoles}
            onToggleRole={toggleRole}
            onClearRoleFilter={clearRoleFilter}
            roleOptions={roleOptions}
            updateCapacityOptimistically={updateCapacityOptimistically}
            updateEmployeeCapacity={updateEmployeeCapacity}
            updateEmployeeRoles={updateEmployeeRoles}
            updateRolesOptimistically={updateRolesOptimistically}
            updateNameOptimistically={updateNameOptimistically}
            updateEmployeeName={updateEmployeeName}
            updateNicknameOptimistically={updateNicknameOptimistically}
            updateEmployeeNickname={updateEmployeeNickname}
            onEmployeeClick={onEmployeeClick}
            selectedEmployeeIds={selectedEmployeeIds}
            roleEditingDisabled={roleEditingDisabled}
            availabilityMode={availabilityMode}
            onAvailabilityModeChange={
              availabilityToggle ? undefined : setAvailabilityMode
            }
            showDeleteForManual={showDeleteForManual}
            onDeleteEmployee={handleDeleteEmployee}
          />
        </div>
      )}
      {showAddEmployee && (
        <AddEmployeeModal
          onClose={() => setShowAddEmployee(false)}
          onEmployeeAdded={handleEmployeeAdded}
        />
      )}
    </>
  );
}
